import { useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";
import { useChatsSidebarApi } from "@/react/hooks/chatsV2/sidebar/useChatsSidebarApi";
import { useWebSocket } from "@/react/hooks/useWebSocket";
import { BotChatItem } from "@circle-react/components/Modals/MessagingModal/LeftSidebar/List/BotChatItem";
import { usePinnedBotChat } from "@circle-react/components/SettingsApp/CommunityInbox/hooks/usePinnedBotChat";
import { usePunditUserContext } from "@circle-react/contexts";
import { Loader } from "@circle-react-uikit/Loader";
import { ChatRoomsList } from "./ChatRoomsList";
import { NoMessages } from "./Item/NoMessages";
import { ListWrapper } from "./ListWrapper";

const MAX_UNREAD_UUIDS = 2000;

export interface ListProps {
  unreadChatRoomUuids?: string[];
}

interface Params {
  uuid: string;
}

export const List = ({ unreadChatRoomUuids }: ListProps) => {
  const { uuid: chatRoomUuid } = useParams<Params>();
  const { currentCommunityMember, isLoading: isCommunityLoading } =
    usePunditUserContext();

  const {
    chatRooms,
    hasNextPage,
    isLoading: isChatRoomsLoading,
    onEventReceive,
    fetchNextPage,
  } = useChatsSidebarApi({
    openChatRoomUuid:
      chatRoomUuid && chatRoomUuid !== "new" ? chatRoomUuid : null,
    uuids: unreadChatRoomUuids?.slice(0, MAX_UNREAD_UUIDS),
  });

  useWebSocket(
    {
      channel: "ChatRoomChannel",
      onMessageReceive: data => {
        onEventReceive(data, currentCommunityMember?.id);
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: true,
    },
    [chatRooms],
  );

  const { botChat, isLoading: isBotChatLoading } = usePinnedBotChat();
  const shouldShowBotChat = Boolean(botChat);

  const isLoading =
    isCommunityLoading || isChatRoomsLoading || isBotChatLoading;

  const chatRoomWithMessages = useMemo(
    () => chatRooms.filter(chatRoom => chatRoom.last_message),
    [chatRooms],
  );
  const isEmpty = !chatRoomWithMessages?.length && !botChat;

  if (isLoading || !currentCommunityMember) {
    return (
      <ListWrapper>
        <Loader center />
      </ListWrapper>
    );
  }

  if (isEmpty) {
    return (
      <ListWrapper>
        <NoMessages />
      </ListWrapper>
    );
  }

  return (
    <ListWrapper>
      {shouldShowBotChat && <BotChatItem chat={botChat} />}
      <InfiniteScroll
        style={{ height: "100%", overflow: "hidden", marginTop: "8px" }}
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={hasNextPage}
        dataLength={chatRoomWithMessages.length}
        scrollableTarget="scrollable-dms-div"
        loader={<Loader center />}
      >
        <ChatRoomsList
          chatRooms={chatRoomWithMessages}
          currentCommunityMember={currentCommunityMember}
        />
      </InfiniteScroll>
    </ListWrapper>
  );
};
